import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchOrganizationReport, type OrganizationReportRow } from '@/api/adminApi';
import { card, input } from '@/components/ui/design';
import { EmptyRow, money, pill, rowBorder, tableWrap, td, th } from './shared';

type SortKey = 'orgName' | 'events' | 'orders' | 'tickets' | 'revenue' | 'averageOrderValue';

const COLUMNS: Array<{ key: SortKey; label: string; numeric: boolean }> = [
    { key: 'orgName', label: 'Organization', numeric: false },
    { key: 'events', label: 'Events', numeric: true },
    { key: 'orders', label: 'Orders', numeric: true },
    { key: 'tickets', label: 'Tickets', numeric: true },
    { key: 'revenue', label: 'Revenue', numeric: true },
    { key: 'averageOrderValue', label: 'Avg order', numeric: true },
];

const Bar: React.FC<{ value: number; max: number }> = ({ value, max }) => (
    <div className="mt-1 h-1.5 w-full min-w-[80px] rounded-full bg-grey95 dark:bg-white/10">
        <div className="h-1.5 rounded-full bg-primary dark:bg-nightAccent" style={{ width: `${max > 0 ? Math.max(2, (value / max) * 100) : 0}%` }} />
    </div>
);

export default function ReportTab() {
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('revenue');
    const [desc, setDesc] = useState(true);
    const { data, isLoading, isError } = useQuery({
        queryKey: ['admin-report', from, to],
        queryFn: () => fetchOrganizationReport({ from: from || undefined, to: to || undefined }),
    });

    const rows = useMemo(() => {
        const list = [...(data?.rows ?? [])];
        list.sort((a: OrganizationReportRow, b: OrganizationReportRow) => {
            const result = sortKey === 'orgName' ? a.orgName.localeCompare(b.orgName) : a[sortKey] - b[sortKey];
            return desc ? -result : result;
        });
        return list;
    }, [data, sortKey, desc]);
    const max = useMemo(() => ({
        events: Math.max(0, ...rows.map(r => r.events)),
        orders: Math.max(0, ...rows.map(r => r.orders)),
        tickets: Math.max(0, ...rows.map(r => r.tickets)),
        revenue: Math.max(0, ...rows.map(r => r.revenue)),
        averageOrderValue: Math.max(0, ...rows.map(r => r.averageOrderValue)),
    }), [rows]);

    const sortBy = (key: SortKey) => {
        if (key === sortKey) setDesc(d => !d);
        else { setSortKey(key); setDesc(key !== 'orgName'); }
    };
    const totals = data?.totals;
    const tiles = [
        { label: 'Organizations', value: totals?.organizations ?? 0 },
        { label: 'Events', value: totals?.events ?? 0 },
        { label: 'Orders', value: totals?.orders ?? 0 },
        { label: 'Revenue', value: money(totals?.revenue ?? 0) },
    ];

    return (
        <div>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
                <label className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">From
                    <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} className={`${input} mt-1`} />
                </label>
                <label className="text-sm font-semibold text-grey20 dark:text-nightTextSecondary">To
                    <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} className={`${input} mt-1`} />
                </label>
                {(from || to) && <button type="button" className="h-12 text-sm font-semibold underline" onClick={() => { setFrom(''); setTo(''); }}>Clear dates</button>}
            </div>
            <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                {tiles.map(tile => (
                    <div key={tile.label} className={`${card} p-4`}>
                        <div className="text-xs font-bold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted">{tile.label}</div>
                        <div className="mt-1 text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">{typeof tile.value === 'number' ? tile.value.toLocaleString() : tile.value}</div>
                    </div>
                ))}
            </div>
            {isError && <p className="mb-3 text-sm text-red-500">Failed to load the report.</p>}
            <p className="mb-2 text-xs text-grey40 dark:text-nightMuted">Orders and revenue count completed orders only. Revenue is ticket sales after discounts, before fees and tax. Events exclude drafts. Dates filter by when the order or event was created.</p>
            <div className={tableWrap}>
                <table className="w-full min-w-[820px]">
                    <thead>
                        <tr>
                            {COLUMNS.map(column => (
                                <th key={column.key} className={`${th} ${column.numeric ? 'text-right' : ''}`} aria-sort={sortKey === column.key ? (desc ? 'descending' : 'ascending') : 'none'}>
                                    <button type="button" onClick={() => sortBy(column.key)} className="font-bold uppercase tracking-[0.12em]">
                                        {column.label}{sortKey === column.key ? (desc ? ' ↓' : ' ↑') : ''}
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.organizerId} className={rowBorder}>
                                <td className={td}>
                                    <span className="font-semibold">{row.orgName}</span>
                                    {row.testMode && <span className={`${pill} ml-2`}>Test mode</span>}
                                </td>
                                <td className={`${td} text-right`}>{row.events.toLocaleString()}<Bar value={row.events} max={max.events} /></td>
                                <td className={`${td} text-right`}>{row.orders.toLocaleString()}<Bar value={row.orders} max={max.orders} /></td>
                                <td className={`${td} text-right`}>{row.tickets.toLocaleString()}<Bar value={row.tickets} max={max.tickets} /></td>
                                <td className={`${td} text-right font-semibold`}>{money(row.revenue)}<Bar value={row.revenue} max={max.revenue} /></td>
                                <td className={`${td} text-right`}>{money(row.averageOrderValue)}<Bar value={row.averageOrderValue} max={max.averageOrderValue} /></td>
                            </tr>
                        ))}
                        {rows.length === 0 && <EmptyRow colSpan={6} loading={isLoading} text="No data for this period." />}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
